import React, { Dispatch } from 'react';
import { AxiosError } from 'axios';

import { apiClientJava, fetchClientJava } from '../../../state';
import { Props, State, reqBody, AssortmentData } from './protopog.types';
import { Pogs } from '../ProtoPogPage.types';
import {
  GetAssortmentParams,
  GetDefaultPogMetricsParams,
  GetAvailableAislesParams,
  GetFileParams,
  GetProtoPogFileParams,
  ProtoLinearParams
} from '../../../types/requests';
import { apiActions } from '../../../state/api/api.actions';
import { protoLinearActions } from '../../../state/protoLinear/protoLinear.actions';
import { GetParametersResponse } from '../../../types/responses';
import { mergeCurrentStateProduct } from '../../../helpers/data';
import { generateUniqueId, openFile } from '../../../helpers/utils';
import { LabelType } from '../../../types/models';
import { AppRoutes } from '../../../utils/constants';
import { openInNewTab } from '../../../utils/global.utils';
import { ProtoLinearState } from '../../../state/protoLinear/protoLinear.types';
import { PogActionType } from '../TableProtoPOG/TableProtoPOG.types';
import { SeedDownloadFields, trackSeedDownload } from '../../../telemetry';

export const protoPogContext = React.createContext<any>({});
export class ProtoPogProvider extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.getProtoPogItems = this.getProtoPogItems.bind(this);
    this.setFilteredPogs = this.setFilteredPogs.bind(this);
    this.getConfigurationParameters = this.getConfigurationParameters.bind(this);
    this.getAssortmentOnlyData = this.getAssortmentOnlyData.bind(this);
    this.getProductsAssortment = this.getProductsAssortment.bind(this);
    this.resetCashedPogValues = this.resetCashedPogValues.bind(this);
    this.getAisles = this.getAisles.bind(this);
    this.getPogFile = this.getPogFile.bind(this);
    this.getPEGxFile = this.getPEGxFile.bind(this);
    this.getFile = this.getFile.bind(this);
    this.getProtoPogFile = this.getProtoPogFile.bind(this);
    this.setApiStart = this.setApiStart.bind(this);
    this.setApiFinish = this.setApiFinish.bind(this);
    this.getProtoLinear = this.getProtoLinear.bind(this);

    this.state = {
      context: {
        api: [],
        aisles: [],
        searchedPogs: [],
        filteredPogs: [],
        file: null,
        getAisles: this.getAisles,
        getPogFile: this.getPogFile,
        getPEGxFile: this.getPEGxFile,
        getFile: this.getFile,
        getProtoPogFile: this.getProtoPogFile,
        getProtoPogItems: this.getProtoPogItems,
        setFilteredPogs: this.setFilteredPogs,
        getConfigurationParameters: this.getConfigurationParameters,
        getAssortmentOnlyData: this.getAssortmentOnlyData,
        getProductsAssortment: this.getProductsAssortment,
        resetCashedPogValues: this.resetCashedPogValues,
        getProtoLinear: this.getProtoLinear,
      },
    };
  }

  async getProtoPogItems(reqBody: reqBody) {
    this.setState((prevState: State) => ({
      context: {
        ...prevState.context,
        protoPogLoading: true,
      },
    }));

    try {
      const response: Pogs = await apiClientJava.getProtoPogs(reqBody);
      const responseKeys = response.map(p => ({ ...p, key: generateUniqueId() }));

      this.setState((prevState: State) => {
        return ({
          context: {
            ...prevState.context,
            searchedPogs: responseKeys,
            filteredPogs: responseKeys,
            protoPogLoading: false,
          },
        });
      });
    } catch {
      this.setState((prevState: State) => ({
        context: {
          ...prevState.context,
          searchedPogs: [],
          filteredPogs: [],
          protoPogLoading: false,
        },
      }));
    }
  }

  async getConfigurationParameters({ storeNumber, aisleCode }: GetAssortmentParams, dispatch: Dispatch<any>) {
    dispatch(apiActions.getAssortmentParams.request());

    try {
      const response: GetParametersResponse = await apiClientJava.getParameters({ storeNumber, aisleCode });

      dispatch?.(apiActions.getAssortmentParams.success(response));

      const {
        aisle_geometry: { blocks: shelfSetup },
        demand,
        side: orientation,
        use_iss: includeISS,
        use_prom: includePromo,
        mirroring_height: mirroringHeight,
        include_display: includeDisplay,
        enable_assortment_sweep: enableAssortmentSweep,
      } = response;
      const assortmentParamsData = { shelfSetup, demand, orientation, includeISS, includePromo, mirroringHeight, includeDisplay, enableAssortmentSweep };

      this.setState((prevState: State) => ({
        context: {
          ...prevState.context,
          assortmentConfig: {
            [`${storeNumber}-${aisleCode}`]: assortmentParamsData,
          },
        },
      }));

      return assortmentParamsData;
    } catch (err) {
      dispatch?.(apiActions.getAssortmentParams.failure(err as AxiosError));
    }
  }

  async getAssortmentOnlyData({ storeNumber, aisleCode }: GetAssortmentParams, dispatch: Dispatch<any>): Promise<AssortmentData | void> {
    dispatch(apiActions.getAssortmentOnly.request());

    try {
      const response = await apiClientJava.getAssortmentOnly({ storeNumber, aisleCode });

      dispatch?.(apiActions.getAssortmentOnly.success(response));

      const { assortmentApi: { store_default: allINSKUs, protos }} = response;

      this.setState((prevState: State) => ({
        context: {
          ...prevState.context,
          SKUs: {
            [`${storeNumber}-${aisleCode}`]: { allINSKUs, protos },
          },
        },
      }));

      return { allINSKUs, protos };
    } catch (err) {
      dispatch?.(apiActions.getAssortmentOnly.failure(err as AxiosError));
    }
  }

  async getProtoLinear (
    params: ProtoLinearParams,
    dispatch: Dispatch<any>,
  ): Promise<ProtoLinearState | void> {
    dispatch(protoLinearActions.getProtoLinear.request(params));

    try {
      const { aisleCode, orientation, label = LabelType.ESL } = params;
      const keyId = `${aisleCode}-${orientation}-${label}`;
      const response = await apiClientJava.getProtoLinear(params);

      dispatch(protoLinearActions.getProtoLinear.success(response));

      this.setState((prevState: State) => ({
        context: {
          ...prevState.context,
          protoLinear: {
            [keyId]: response,
          },
        },
      }));

      return response;
    } catch (err) {
      dispatch(protoLinearActions.getProtoLinear.failure(err as AxiosError));
    }
  }

  async getProductsAssortment (
    params: GetDefaultPogMetricsParams,
    dispatch: Dispatch<any>,
  ) {
    dispatch(apiActions.getCurrentStateProductsAssortment.request());

    try {
      const keyId = `${params.storeNumber}-${params.aisleCode}`;
      const response = await apiClientJava.getDefaultProductMetrics(params);

      dispatch(apiActions.getCurrentStateProductsAssortment.success(response));

      this.setState((prevState: State) => ({
        context: {
          ...prevState.context,
          baseAssortment: {
            [keyId]: mergeCurrentStateProduct(prevState.context.baseAssortment?.[keyId] || [], response),
          },
        },
      }));

      return response;
    } catch (err) {
      dispatch(apiActions.getCurrentStateProductsAssortment.failure(err as AxiosError));
    }
  }

  async getAisles(params: GetAvailableAislesParams) {
    try {
      const response = await apiClientJava.getAvailableAisles(params);

      this.setState((prevState: State) => ({
        context: {
          ...prevState.context,
          aisles: response,
        },
      }));

      return response;
    } catch (err) {
      console.log(err);
    }
  }

  async getFile(getFileParams: GetFileParams, apiName: string) {
    try {
      this.setApiStart(apiName);

      return await fetchClientJava.getFile(getFileParams);
    } catch (err) {
      console.log(err);
    } finally {
      this.setApiFinish(apiName);
    }
  }

  async getPEGxFile(params: GetFileParams, analytics?: SeedDownloadFields) {
    const file = await this.getFile(params, PogActionType.PEGX + params.key);

    // Connect+ only: there is no jobId, so the backend cannot enrich this event.
    trackSeedDownload({
      downloadType: 'PEGX_PDF',
      jobId: '',
      pogKind: 'PDF_PROTO',
      ...analytics,
      success: Boolean(file),
    });

    const fileURL = URL.createObjectURL(file);

    openInNewTab(AppRoutes.PEGx, { fileURL });
  }

  async getPogFile(params: GetFileParams) {
    const file = await this.getFile(params, PogActionType.GET_FILE + params.key);
    const { name } = params;

    openFile(file, name);
  }

  // TODO the same getPogFile
  /** Resolves to whether a file was opened; callers track it (CONNECT_PLUS_PDF vs PROTO_EXPORT). */
  async getProtoPogFile(params: GetProtoPogFileParams): Promise<boolean> {
    const { key, name } = params;
    let apiName = '';

    apiName = 'getProtoPogFile' + key;

    try {
      this.setApiStart(apiName);
      const file = await fetchClientJava.getProtoPogFile(params);

      openFile(file, name);

      return Boolean(file);
    } catch (err) {
      console.log(err);

      return false;
    } finally {
      this.setApiFinish(apiName);
    }
  }

  setApiStart(apiName: string) {
    this.setState((prevState: State) => ({
      context: {
        ...prevState.context,
        api: prevState.context.api.concat(apiName),
      },
    }));
  }

  setApiFinish(apiName: string) {
    this.setState((prevState: State) => ({
      context: {
        ...prevState.context,
        api: prevState.context.api.filter(api => api !== apiName),
      },
    }));
  }

  setFilteredPogs(pogs: any) {
    this.setState((prevState: State) => ({
      context: {
        ...prevState.context,
        filteredPogs: pogs
      },
    }));
  }

  resetCashedPogValues() {
    this.setState((prevState: State) => ({
      context: {
        ...prevState.context,
        assortmentConfig: {},
        baseAssortment: {},
        SKUs: {},
      },
    }));
  }

  render(): JSX.Element {
    return (
      <protoPogContext.Provider value={this.state.context}>
        {this.props.children}
      </protoPogContext.Provider>
    );
  }
}
